import React from 'react';

import {Button} from '@kit';

import {cls} from '../../../../components/utils';
import {ConditionType} from '../../../../generated/types';
import {useCharacter} from '../character-context';
import {MAX_EXHAUSTION} from '../model/character-state';
import {activeConditions, conditionSummary} from '../model/conditions';
import {conditionLabel} from '../model/labels';

import {formatFeet} from './format';
import {SheetBlock} from './sheet-block';

import css from './sheet.module.css';

const CONDITIONS = Object.values(ConditionType)
	.filter((condition) => condition !== ConditionType.Exhaustion)
	.sort((first, second) => conditionLabel(first).localeCompare(conditionLabel(second)));

export const ConditionsBar: React.FC = () => (
	<SheetBlock title="Состояния">
		<ConditionsList />
	</SheetBlock>
);

/** переключатели состояний, утомление, действующие заклинания и их эффекты */
export const ConditionsList: React.FC = () => {
	const {catalog, state, character, update} = useCharacter();
	const {exhaustion} = character;

	const toggle = (condition: string) =>
		update((current) => ({
			conditions: current.conditions.includes(condition)
				? current.conditions.filter((active) => active !== condition)
				: [...current.conditions, condition],
		}));

	const setExhaustion = (value: number) => update({exhaustion: Math.max(0, Math.min(MAX_EXHAUSTION, value))});
	const effects = activeConditions(state.conditions)
		.map((condition) => ({condition, summary: conditionSummary(condition)}))
		.filter(({summary}) => summary.length);

	return (
		<div className={css.conditions}>
			{CONDITIONS.map((condition) => {
				const isActive = state.conditions.includes(condition);
				return (
					<button
						key={condition}
						type="button"
						aria-pressed={isActive}
						className={cls(css.condition, isActive && css.conditionActive)}
						onClick={() => toggle(condition)}
					>
						{conditionLabel(condition)}
					</button>
				);
			})}
			<span className={cls(css.condition, css.exhaustion, exhaustion > 0 && css.conditionActive)}>
				<Button
					size="small"
					appearance="transparent"
					aria-label="Снизить утомление"
					disabled={!exhaustion}
					onClick={() => setExhaustion(exhaustion - 1)}
				>
					−
				</Button>
				Утомление {exhaustion}
				<Button
					size="small"
					appearance="transparent"
					aria-label="Повысить утомление"
					disabled={exhaustion >= MAX_EXHAUSTION}
					onClick={() => setExhaustion(exhaustion + 1)}
				>
					+
				</Button>
			</span>
			{(state.activeSpells || []).map(({spellId}) => (
				<button
					key={spellId}
					type="button"
					className={cls(css.condition, css.activeSpell)}
					title="Закончить действие заклинания"
					onClick={() =>
						update((current) => ({
							activeSpells: (current.activeSpells || []).filter((active) => active.spellId !== spellId),
						}))
					}
				>
					{catalog.getSpell(spellId)?.title || spellId} ×
				</button>
			))}
			{(exhaustion > 0 || effects.length > 0) && (
				<ul className={css.conditionEffects}>
					{effects.map(({condition, summary}) => (
						<li key={condition}>
							<b>{conditionLabel(condition)}:</b> {summary.join('; ')}
						</li>
					))}
					{exhaustion > 0 && (
						<li>
							<b>Утомление:</b> −{character.d20Penalty} к броскам d20, скорость −
							{formatFeet(5 * exhaustion)}
						</li>
					)}
				</ul>
			)}
		</div>
	);
};
